/**
 * businessContextApi — thin facade over the enterprise business-context
 * endpoints exposed by ``@ando/core/api``.
 *
 * Two helpers:
 *   - ``updateBusinessContext`` → PUT /enterprise/connectors/{id}/business-context
 *   - ``regenerateBusinessContext`` → POST /enterprise/connectors/{id}/regenerate-context
 *
 * Keeping this surface separate from ``hooks/useBusinessContext`` (which
 * still drives the existing ``BusinessContextEditor`` panel) means the
 * new ``BusinessContextBanner`` reads only from ``useProfilingStatus``
 * — no parallel TanStack query — while still talking to the same BE
 * routes. Mutations are intentionally returned as bare async functions
 * (no hook wrapper) so the banner can compose them into its own
 * optimistic ``setQueryData`` + invalidate flow without dragging the
 * editor's invalidation surface along.
 */

import {
  regenerateEnterpriseBusinessContext,
  updateEnterpriseBusinessContext,
} from "@ando/core/api";

export interface UpdateBusinessContextBody {
  readonly business_context: string;
}

/**
 * PUT the new full text of the business context. Resolves on 204.
 *
 * The body shape mirrors the BE contract verbatim (``{business_context}``)
 * so the call site reads as the wire payload — no field-name translation
 * obscures the contract.
 */
export async function updateBusinessContext(
  connId: string,
  body: UpdateBusinessContextBody,
): Promise<void> {
  await updateEnterpriseBusinessContext(connId, body.business_context);
}

/**
 * POST the regenerate trigger. The BE re-runs the LLM summarisation in
 * the background; the new text appears on the next ``/profiling-status``
 * poll, so callers should invalidate the profiling cache after this
 * resolves.
 *
 * Wall-clock 10-30s. The HTTP response itself returns immediately with
 * the freshly-generated text, but we drop the body here — the banner
 * source-of-truths from the profiling cache to keep one update surface.
 */
export async function regenerateBusinessContext(connId: string): Promise<void> {
  await regenerateEnterpriseBusinessContext(connId);
}
